import { Controller } from "@hotwired/stimulus";

/**
 * Category Combobox Controller
 *
 * Type-to-filter dropdown of existing categories with an inline
 * "+ Create '<typed>'" affordance. Maintains two hidden form fields:
 *   - category_id: set when an existing option is selected
 *   - new_category_name: set when the typed text doesn't match any option
 *
 * Usage:
 *   <div data-controller="category-combobox"
 *        data-category-combobox-options-value='[{"id":1,"name":"Hair"}, ...]'>
 *     <input data-category-combobox-target="input"
 *            data-action="input->category-combobox#filter focus->category-combobox#open">
 *     <input type="hidden" name="service[category_id]"
 *            data-category-combobox-target="categoryId">
 *     <input type="hidden" name="service[new_category_name]"
 *            data-category-combobox-target="newName">
 *     <ul data-category-combobox-target="list" class="hidden"></ul>
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "categoryId", "newName", "list"];
  static values = { options: Array };

  connect() {
    this.boundOutsideClick = this.closeOnOutside.bind(this);
    document.addEventListener("click", this.boundOutsideClick);
    this.syncHiddenFields();
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick);
  }

  open() {
    this.renderList(this.inputTarget.value);
    this.listTarget.classList.remove("hidden");
  }

  close() {
    this.listTarget.classList.add("hidden");
  }

  closeOnOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  filter(event) {
    const query = event.target.value;
    this.renderList(query);
    this.listTarget.classList.remove("hidden");
    this.syncHiddenFields();
  }

  selectOption(event) {
    const button = event.currentTarget;
    const id = button.dataset.categoryId || "";
    const name = button.dataset.categoryName || "";

    this.inputTarget.value = name;
    this.categoryIdTarget.value = id;
    this.newNameTarget.value = id ? "" : name;
    this.close();
  }

  renderList(query) {
    const trimmed = (query || "").trim();
    const lowered = trimmed.toLowerCase();
    const options = this.optionsValue || [];

    const matches = options.filter((opt) =>
      opt.name.toLowerCase().includes(lowered)
    );

    const exact = options.find(
      (opt) => opt.name.toLowerCase() === lowered
    );

    const html = [];

    matches.forEach((opt) => {
      html.push(
        `<li role="option">
          <button type="button"
                  data-action="click->category-combobox#selectOption"
                  data-category-id="${opt.id}"
                  data-category-name="${this.escape(opt.name)}"
                  class="w-full text-left px-3 py-2 text-sm text-black hover:bg-gray-50 cursor-pointer">
            ${this.escape(opt.name)}
          </button>
        </li>`
      );
    });

    if (trimmed.length > 0 && !exact) {
      if (matches.length > 0) {
        html.push(`<li role="separator" aria-hidden="true" class="border-t border-gray-200 my-1"></li>`);
      }
      html.push(
        `<li role="option">
          <button type="button"
                  data-action="click->category-combobox#selectOption"
                  data-category-id=""
                  data-category-name="${this.escape(trimmed)}"
                  class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
            <span class="text-gray-500">+ Create</span>
            <span class="font-medium text-black">"${this.escape(trimmed)}"</span>
          </button>
        </li>`
      );
    }

    if (html.length === 0) {
      html.push(
        `<li class="px-3 py-2 text-sm text-gray-500">No categories yet — type a name to create one.</li>`
      );
    }

    this.listTarget.innerHTML = html.join("");
  }

  // Keep hidden fields in sync with the current input text — handles the case
  // where the user types but doesn't select from the list before submitting.
  syncHiddenFields() {
    const value = (this.inputTarget.value || "").trim();
    if (!value) {
      this.categoryIdTarget.value = "";
      this.newNameTarget.value = "";
      return;
    }

    const options = this.optionsValue || [];
    const match = options.find(
      (opt) => opt.name.toLowerCase() === value.toLowerCase()
    );

    if (match) {
      this.categoryIdTarget.value = match.id;
      this.newNameTarget.value = "";
    } else {
      this.categoryIdTarget.value = "";
      this.newNameTarget.value = value;
    }
  }

  escape(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}
